// @flow
import type { Store } from "redux";

import type ErrorResponse from "../models/error/ErrorResponse";

import type FormModel from "../models/form/FormModel";
import typeof {
  NOTIFICATION_TYPES,
  AUTOSAVE_STATUS,
} from "../constants/Constants";

import type { MessageObject } from "../i18n/types";
import type {
  I18nState,
  UpdateLocaleAction,
  SetLocalesAction,
} from "./_i18n/types";
import type {
  ModularUIState,
  ModularUIAction,
  UpdateStatusAction,
  SetModelAction,
  UpdateModelAction,
  UpdateFormAction,
  RemoveModelByKeyAction,
  ResetModularUIAction,
  InitModelAction,
} from "./_modularui/types";
import type {
  RouterState,
  LocationChangeAction,
  PushAction,
  ReplaceAction,
  GoAction,
  GoBackAction,
  GoForwardAction,
} from "./_router/types";

export type PreferenceValue =
  | null
  | string
  | boolean
  | { [key: string]: any }
  | Array<PreferenceValue>;

export type UpdateFormOptions = {
  autosubmit: boolean,
  autosave: boolean,
  autoupdate: boolean,
  forceUpdate?: boolean,
  /** Default true: Activate/deactivate the form object validate by an update */
  validate?: boolean,
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 */
export type NoAction = {
  type: "NO_ACTION",
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useErrorHandler) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type SaveErrorAction = {
  type: "SAVE_ERROR",
  payload: ErrorResponse,
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useModal) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type ShowModalAction = {
  type: "SHOW_MODAL",
  payload: string,
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useModal) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type CloseModalAction = {
  type: "CLOSE_MODAL",
  payload: string,
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. onDismiss: () => void`.
 * - Use the appropriate hook (useNotification) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type DismissNotificationAction = {
  type: "DISMISS_NOTIFICATION",
};
/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useNotification) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type ShowNotificationAction = {
  type: "SHOW_NOTIFICATION",
  payload: {
    type: $Keys<NOTIFICATION_TYPES>,
    message: MessageObject,
    error: ?ErrorResponse,
  },
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (usePreference) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type SetPreferenceAction = {
  type: "SET_PREFERENCE",
  payload: { [name: string]: PreferenceValue },
};
/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (usePreference) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type SetPreferencesAction = {
  type: "SET_PREFERENCES",
  payload: { [name: string]: PreferenceValue },
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useProgressIndicator) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type StartProgressAction = {
  type: "START_PROGRESS",
};
/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useProgressIndicator) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type FinishProgressAction = {
  type: "FINISH_PROGRESS",
};
/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useProgressIndicator) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type ResetProgressAction = {
  type: "RESET_PROGRESS",
};
/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useProgressIndicator) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type UpdateProgressAction = {
  type: "UPDATE_PROGRESS",
  payload: { percentComplete: number },
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useAuthentication) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type SendAuthenticationErrorAction = {
  type: "AUTHENTICATION_ERROR",
  payload: string,
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useAuthentication) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type ResetAuthErrorsAction = {
  type: "AUTHENTICATION_RESET_ERRORS",
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useAuthentication) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type LoginSuccessAction = {
  type: "AUTHENTICATION_SUCCESS",
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useAuthentication) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type ChangePasswordAction = {
  type: "CHANGE_PASSWORD",
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useAuthentication) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type LogoutSuccessAction = {
  type: "AUTHENTICATION_LOGOUT",
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook (useFormNavigation) to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type UpdateAutosaveAction = {
  type: "UPDATE_AUTOSAVE_STATUS",
  payload: {
    status: $Keys<AUTOSAVE_STATUS>,
    model: FormModel,
  },
};

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook to encapsulate this logic.
 * - Reference the action object type for middleware, use ReturnType<typeof actionCreator>.
 */
export type UpdateEntryDateAction = {
  type: "UPDATE_ENTRYDATE",
  payload: ISO_DATE,
};

/**
 * @deprecated This union type is a legacy pattern and is not maintained for new actions.
 */
export type ReduxAction =
  | UpdateStatusAction
  | SetModelAction
  | InitModelAction
  | UpdateModelAction
  | UpdateFormAction
  | RemoveModelByKeyAction
  | ResetModularUIAction
  | SaveErrorAction
  | UpdateLocaleAction
  | SetLocalesAction
  | ShowModalAction
  | CloseModalAction
  | DismissNotificationAction
  | ShowNotificationAction
  | SetPreferenceAction
  | SetPreferencesAction
  | StartProgressAction
  | FinishProgressAction
  | ResetProgressAction
  | UpdateProgressAction
  | ResetAuthErrorsAction
  | SendAuthenticationErrorAction
  | LoginSuccessAction
  | ChangePasswordAction
  | LogoutSuccessAction
  | UpdateAutosaveAction
  | UpdateEntryDateAction
  | LocationChangeAction
  | PushAction
  | ReplaceAction
  | GoAction
  | GoBackAction
  | GoForwardAction
  | ModularUIAction
  | NoAction;

export type AuthState = {
  +mustChangePassword: boolean,
  +error: ?string,
};

export type ErrorState = null | ErrorResponse;

export type ModalState = {
  +key: string,
  +visible: boolean,
  +size?: string,
};

export type ModalsState = {
  +modals: Array<ModalState>,
};

export type NotificationState = {
  +render: boolean,
  +messageType: string | null,
  +message: MessageObject | null,
  +error: ?ErrorResponse | null,
};

export type PreferencesState = {
  +[name: string]: PreferenceValue,
};

export type ProgressIndicatorState = {
  +count: number,
  +timestamp: number,
  +percentComplete: number,
};

export type ModelCatalogState = {
  entryDate: ISO_DATE | null,
};

export type ReduxState = {
  +router: RouterState,
  +modularui: ModularUIState,
  +i18n: I18nState,
  +auth: AuthState,
  +modelcatalog: ModelCatalogState,
  +error: ErrorState,
  +modals: ModalsState,
  +notification: NotificationState,
  +progressindicator: ProgressIndicatorState,
  +preferences: PreferencesState,
  ...
};

export type GetState = () => ReduxState;

export type Subscribe = (listener: () => void) => () => void;

export type ThunkExtra = {
  subscribe: Subscribe,
  ...
};

export type ThunkAction<R = any> = (
  dispatch: Dispatch,
  getState: GetState,
  extra: ThunkExtra,
) => R;

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook to encapsulate this logic.
 */
export type PromiseAction = Promise<ReduxAction | ThunkAction<any>>;

export type Dispatch = (
  action: ReduxAction | ThunkAction<any> | PromiseAction,
) => any;

export type ReduxStore = Store<ReduxState, ReduxAction, Dispatch>;

/**
 * @deprecated This manual action type will be removed in a future major release.
 * Recommended fixes:
 * - Type component props as a simple callback: e.g. `onDismiss: () => void`.
 * - Use the appropriate hook to encapsulate this logic.
 */
export type PossibleAction = ReduxAction | ThunkAction<any> | PromiseAction;

export type CustomReducers = { [reducerKey: string]: any };
